import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Gift } from 'lucide-react';
import styles from './form.module.css';

// The outcome is always decided server-side (see responseController.submitResponse) so it
// can't be gamed from the client, and is locked in after one scratch — there is no
// "update response" flow here, unlike most other activity types.
//
// Interaction: one face-down tile per configured outcome, laid out in a shuffled/scattered
// deal (not a plain grid) so it reads as "pick a card" rather than one ambiguous surface.
// The participant taps exactly one; that tile alone plays an auto-scratch reveal animation
// (a real physical drag-to-scratch was tried and never read as clearly, on real devices, as
// "the user chose and revealed something" — a tap-to-pick model sidesteps that entirely)
// while the server's already-drawn outcome comes back, then shows the prize on that tile.
// The other tiles never get an outcome assigned — since only one tile is ever revealed, the
// others don't need one; they just dim out once a pick has been made.

const AUTO_SCRATCH_DURATION_MS = 750;
const ROW_COUNT = 5;
const SHUFFLE_DURATION_MS = 1150;
const POPPER_PARTICLES_PER_SIDE = 18;
const POPPER_COLORS = ['#C5F135', 'var(--cat-amber)', 'var(--cat-pink)', 'var(--cat-teal)', 'var(--cat-violet)', 'var(--cat-blue)'];
const POPPER_VISIBLE_MS = 1900;

// Metallic coating fill — fixed colors (not theme tokens): a real scratch card's foil
// looks the same regardless of the app's light/dark theme.
const COATING_GRADIENT_STOPS = ['#eef1f4', '#c7ccd3', '#dde1e6', '#aab1bb'];

const lerp = (a, b, t) => a + (b - a) * t;

const shuffledIndices = (n) => {
    const arr = Array.from({ length: n }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
};

function paintCoating(ctx, width, height) {
    ctx.globalCompositeOperation = 'source-over';
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    COATING_GRADIENT_STOPS.forEach((color, i) => gradient.addColorStop(i / (COATING_GRADIENT_STOPS.length - 1), color));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Subtle grain — a flat gradient alone reads as an obviously-digital fill; scattered
    // light/dark flecks at low opacity give it a metallic-foil texture instead.
    const grainCount = Math.round((width * height) / 180);
    for (let i = 0; i < grainCount; i++) {
        const shade = Math.random() > 0.5 ? 255 : 0;
        ctx.fillStyle = `rgba(${shade},${shade},${shade},${(0.04 + Math.random() * 0.08).toFixed(3)})`;
        ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }
}

// Plays a ~750ms simulated scratch-off directly on a mounted <canvas> — full-width
// horizontal passes revealed in shuffled order with jitter, so it reads as an uneven hand
// scratching rather than a mechanical wipe, while still guaranteeing complete coverage.
function playAutoScratch(canvas, onDone) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) { onDone(); return () => {}; }
    canvas.width = rect.width;
    canvas.height = rect.height;
    const ctx = canvas.getContext('2d');
    paintCoating(ctx, rect.width, rect.height);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const rowHeight = rect.height / ROW_COUNT;
    const strokeWidth = rowHeight * 1.15;
    const overshoot = strokeWidth * 0.6;
    const order = shuffledIndices(ROW_COUNT);
    const rows = order.map((rowIndex, orderPos) => {
        const jitterY = (Math.random() - 0.5) * rowHeight * 0.3;
        const leftToRight = orderPos % 2 === 0;
        return {
            y: rowHeight * (rowIndex + 0.5) + jitterY,
            x1: leftToRight ? -overshoot : rect.width + overshoot,
            x2: leftToRight ? rect.width + overshoot : -overshoot,
        };
    });
    const segment = 1 / ROW_COUNT;

    let frameId;
    const start = performance.now();
    const step = (now) => {
        const t = Math.min((now - start) / AUTO_SCRATCH_DURATION_MS, 1);
        const activeIndex = Math.min(Math.floor(t / segment), ROW_COUNT - 1);
        for (let i = 0; i <= activeIndex; i++) {
            const row = rows[i];
            const localT = i < activeIndex ? 1 : Math.min((t - i * segment) / segment, 1);
            const eased = 1 - Math.pow(1 - localT, 3);
            ctx.beginPath();
            ctx.lineWidth = strokeWidth;
            ctx.moveTo(row.x1, row.y);
            ctx.lineTo(lerp(row.x1, row.x2, eased), row.y);
            ctx.stroke();
        }
        if (t < 1) {
            frameId = requestAnimationFrame(step);
        } else {
            ctx.clearRect(0, 0, rect.width, rect.height);
            onDone();
        }
    };
    frameId = requestAnimationFrame(step);
    return () => { if (frameId) cancelAnimationFrame(frameId); };
}

const ScratchCardForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null }) => {
    const outcome = userAnswer?.outcome ?? null;
    const tileCount = Array.isArray(content?.options) ? content.options.filter(o => String(o || '').trim()).length : 0;
    const hasOutcomes = tileCount > 0;

    // 'idle' (no tile picked yet) -> 'scratching' (the picked tile's canvas is animating
    // while the server draw is in flight) -> 'revealed'. Starting in 'revealed' if this
    // participant already has an outcome (e.g. after a page reload) skips the animation
    // and confetti/poppers entirely, and just shows the prize on a single static tile.
    const [phase, setPhase] = useState(outcome != null ? 'revealed' : 'idle');
    const [pickedTile, setPickedTile] = useState(null);
    const [confettiKey, setConfettiKey] = useState(0);
    const [showPoppers, setShowPoppers] = useState(false);
    // Plays a visible shuffle the instant this loads, before the cards are tappable — only
    // for a genuinely fresh card; one already answered (e.g. after a page reload, which
    // starts straight in 'revealed') has nothing to shuffle, so it's skipped entirely.
    const [shuffling, setShuffling] = useState(outcome == null);

    const canvasRef = useRef(null);
    const stopAnimationRef = useRef(null);
    const animationDoneRef = useRef(phase === 'revealed');
    const outcomeArrivedRef = useRef(outcome != null);

    useEffect(() => {
        if (!shuffling) return;
        const timer = setTimeout(() => setShuffling(false), SHUFFLE_DURATION_MS);
        return () => clearTimeout(timer);
        // Deliberately runs once on mount only — re-shuffling on every re-render would
        // replay the animation every time e.g. the outcome prop arrives mid-shuffle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Purely cosmetic "dealt cards" scatter — a small random rotation/offset per tile,
    // frozen for the lifetime of this card (not re-randomized on every render).
    const tileScatter = useMemo(
        () => Array.from({ length: Math.max(tileCount, 1) }, () => ({
            rotate: (Math.random() * 8 - 4).toFixed(1),
            liftMs: Math.round(Math.random() * 150),
        })),
        [tileCount]
    );

    // The shuffle pile's own per-card jitter — separate randoms from tileScatter so the
    // "in the pile" look and the "dealt out" look aren't identical.
    const shuffleCards = useMemo(
        () => Array.from({ length: Math.max(Math.min(tileCount, 6), 1) }, () => ({
            rotate: (Math.random() * 30 - 15).toFixed(1),
            delay: Math.round(Math.random() * 200),
        })),
        [tileCount]
    );

    const interactionDisabled = isPreview || isLocked || !hasOutcomes || phase === 'revealed' || shuffling;

    const tryReveal = useCallback(() => {
        if (animationDoneRef.current && outcomeArrivedRef.current) {
            setPhase('revealed');
            setConfettiKey((k) => k + 1);
        }
    }, []);

    // The server may resolve before or after the tile's animation finishes — either order
    // must lead to a reveal once both are ready.
    //
    // Also handles a restored answer: JoinPage restores userAnswer from sessionStorage
    // asynchronously (it re-fetches the event first), so it can easily arrive *after* this
    // form has already mounted in 'idle'. Without this, the card would sit there mid-shuffle
    // forever even though the participant already has a result — jump straight to it instead,
    // no shuffle/scratch animation or confetti replay, since nothing was just won this instant.
    useEffect(() => {
        if (outcome == null) return;
        outcomeArrivedRef.current = true;
        if (phase === 'idle') {
            setShuffling(false);
            animationDoneRef.current = true;
            setPhase('revealed');
        } else {
            tryReveal();
        }
    }, [outcome, phase, tryReveal]);

    useEffect(() => () => { stopAnimationRef.current?.(); }, []);

    // Fires only on a fresh reveal (confettiKey increments) — not when this participant
    // already had an outcome on mount (e.g. after a page reload).
    useEffect(() => {
        if (confettiKey === 0) return;
        setShowPoppers(true);
        const timer = setTimeout(() => setShowPoppers(false), POPPER_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [confettiKey]);

    // Once the picked tile's canvas mounts, play its scratch animation.
    useEffect(() => {
        if (phase !== 'scratching' || !canvasRef.current) return;
        stopAnimationRef.current = playAutoScratch(canvasRef.current, () => {
            animationDoneRef.current = true;
            tryReveal();
        });
        return () => stopAnimationRef.current?.();
    }, [phase, tryReveal]);

    const pickTile = (index) => {
        if (interactionDisabled || phase !== 'idle') return;
        try { navigator.vibrate?.(15); } catch (_) { /* vibration unsupported/blocked — purely cosmetic */ }
        animationDoneRef.current = false;
        outcomeArrivedRef.current = outcome != null;
        setPickedTile(index);
        setPhase('scratching');
        onSubmit({});
    };

    const handleTileKeyDown = (e, index) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        pickTile(index);
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Pick a card to reveal your prize!'}
                {isLocked && outcome == null && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>{content.settings.descText}</div>
            )}

            {!hasOutcomes ? (
                <div className={`${styles.scratchTile} ${styles.scratchTileIdle}`} style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>
                    <span className={styles.scratchTilePrompt}>Not set up yet</span>
                </div>
            ) : phase === 'revealed' ? (
                // Once revealed — whether from a fresh pick just now or a result restored
                // after a page reload, which never chose a specific tile position — there's
                // nothing left to pick from, so just show the one result tile.
                <div className={`${styles.scratchTile} ${styles.scratchTileRevealed}`} style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>
                    <span className={styles.scratchTileOutcome}>
                        <Gift size={22} aria-hidden />
                        <span>{outcome}</span>
                    </span>
                </div>
            ) : shuffling ? (
                <div className={styles.scratchShuffleStage} aria-hidden="true">
                    {shuffleCards.map((card, i) => (
                        <div
                            key={i}
                            className={styles.scratchShuffleTile}
                            style={{ '--rotate': `${card.rotate}deg`, animationDelay: `${card.delay}ms` }}
                        />
                    ))}
                </div>
            ) : (
                <div className={styles.scratchGrid} role="group" aria-label="Pick a card">
                    {Array.from({ length: tileCount }).map((_, index) => {
                        const isPicked = pickedTile === index;
                        const isScratchingTile = isPicked && phase === 'scratching';
                        const isOtherTile = pickedTile != null && !isPicked;
                        const tileDisabled = interactionDisabled || (pickedTile != null && !isPicked);
                        const scatter = tileScatter[index] || { rotate: 0, liftMs: 0 };

                        return (
                            <div
                                key={index}
                                role="button"
                                aria-disabled={tileDisabled}
                                aria-label={`Card ${index + 1}`}
                                tabIndex={tileDisabled ? -1 : 0}
                                className={`${styles.scratchTile} ${
                                    isScratchingTile ? styles.scratchTileScratching : styles.scratchTileIdle
                                } ${isOtherTile ? styles.scratchTileDimmed : ''}`}
                                style={!isPicked && pickedTile == null ? {
                                    '--rotate': `${scatter.rotate}deg`,
                                    animationDelay: `${scatter.liftMs}ms`,
                                } : undefined}
                                onClick={() => pickTile(index)}
                                onKeyDown={(e) => handleTileKeyDown(e, index)}
                            >
                                {isScratchingTile ? (
                                    <>
                                        <span className={styles.scratchTileOutcome}>
                                            <Gift size={22} aria-hidden />
                                            <span>{outcome ?? '···'}</span>
                                        </span>
                                        <canvas ref={canvasRef} className={styles.scratchTileCanvas} aria-hidden="true" />
                                    </>
                                ) : (
                                    <span className={styles.scratchTilePrompt}>?</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {shuffling && <p className={styles.submittedHint}>Shuffling…</p>}
            {!shuffling && phase === 'scratching' && <p className={styles.submittedHint}>Revealing your card…</p>}
            {!shuffling && phase === 'revealed' && <p className={styles.submittedHint}>You've already picked a card — one try per participant.</p>}

            <PopperCelebration show={showPoppers} />
        </div>
    );
};

// Two party-popper bursts from the bottom corners, shooting confetti up and inward across
// the whole viewport — a much bigger, more obvious "you won something" moment than the
// tile itself could give. Portaled to <body> so it isn't clipped by any scroll container.
function PopperCelebration({ show }) {
    if (!show || typeof document === 'undefined') return null;
    return createPortal(
        <div className={styles.popperOverlay} aria-hidden="true">
            {['left', 'right'].map((side) => (
                <div key={side} className={side === 'left' ? styles.popperOriginLeft : styles.popperOriginRight}>
                    {Array.from({ length: POPPER_PARTICLES_PER_SIDE }).map((_, i) => {
                        const baseAngle = side === 'left' ? -60 : -120;
                        const spread = 55;
                        const angle = baseAngle + (Math.random() * spread - spread / 2);
                        const distance = 260 + Math.random() * 260;
                        const size = 6 + Math.round(Math.random() * 6);
                        const isCircle = i % 2 === 0;
                        return (
                            <span
                                key={i}
                                className={styles.popperPiece}
                                style={{
                                    '--angle': `${angle}deg`,
                                    '--distance': `${distance}px`,
                                    '--delay': `${Math.random() * 180}ms`,
                                    '--duration': `${1200 + Math.random() * 500}ms`,
                                    '--spin': `${(Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 360)}deg`,
                                    width: `${size}px`,
                                    height: `${isCircle ? size : size * 1.8}px`,
                                    borderRadius: isCircle ? '50%' : '1px',
                                    background: POPPER_COLORS[i % POPPER_COLORS.length],
                                }}
                            />
                        );
                    })}
                </div>
            ))}
        </div>,
        document.body
    );
}

export default ScratchCardForm;
